<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { TOOLTIP_FOR_RELATED_NEWS } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import dayjs from 'dayjs';
import AdpDocumentCardSmall from '@/components/common/article/adp-document-card-small.vue';
import { AdpDocument, Theme } from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { useThemeRelatedFeed } from '@/utils/swr';
import { useEmitter, useStore } from '@/utils/vue';

const { theme } = defineProps<{
  theme: Theme;
}>();

const store = useStore();
const emitter = useEmitter();
const feedType = computed(() => store.state.feedType.feedType);

const feedStartDate = computed(() =>
  theme.feed_last_updated_at ? dayjs(theme.feed_last_updated_at) : dayjs(),
);

// NOTE: feed_typeが増えた場合はBackend側で配列で受け取るように修正する
const { data: domesticData } = useThemeRelatedFeed(
  feedType,
  theme.id,
  'domestic',
  ref(feedStartDate.value.toDate()),
  ref(1),
  2,
);
const { data: foreignData } = useThemeRelatedFeed(
  feedType,
  theme.id,
  'foreign',
  ref(feedStartDate.value.toDate()),
  ref(1),
  2,
);
const articles = computed(() => {
  if (feedType.value === 'all') {
    return domesticData.value && foreignData.value
      ? [
          ...(domesticData.value?.related_feeds ?? []),
          ...(foreignData.value?.related_feeds ?? []),
        ]
      : undefined;
  } else if (feedType.value === 'domestic') {
    return domesticData.value?.related_feeds;
  } else {
    return foreignData.value?.related_feeds;
  }
});

onMounted(() => {
  emitter.on('article-updated', updateArticle);
  emitter.on('article-viewed', updateArticle);
});

onUnmounted(() => {
  emitter.off('article-updated', updateArticle);
  emitter.off('article-viewed', updateArticle);
});

const updateArticle = (updatedArticle: AdpDocument) => {
  if (!articles.value) return;
  syncAdpDocumentActions(articles.value, updatedArticle);
};
</script>

<template>
  <div class="related-news-preview">
    <div class="title c-title-bold--s">
      関連ニュース<DgrIcon
        class="tooltip-icon"
        v-tooltip="TOOLTIP_FOR_RELATED_NEWS"
        name="question-circle"
        size="small"
      />
    </div>
    <template v-if="articles !== undefined">
      <div class="articles" v-for="article in articles" :key="article.id">
        <AdpDocumentCardSmall
          :adp-document="article"
          :theme-id="theme.id"
          :feed-type="feedType"
          page-name="theme"
          feature="related_feed"
        ></AdpDocumentCardSmall>
        <div class="divider"></div>
      </div>
      <div
        class="related-news-page-link c-title-bold--s"
        v-if="articles.length > 0"
      >
        <router-link
          is="button"
          class="c-text-regular--m c-btn c-btn--auto c-btn--small c-btn--outline"
          :to="{ name: 'themeRelatedNews' }"
          >もっと見る</router-link
        >
      </div>
    </template>
    <div class="empty-articles" v-if="articles && articles.length === 0">
      <div class="image-wrap">
        <img class="image" src="@/assets/state-prepare-feed-small.png" />
      </div>
      <div class="message c-text-regular--m">配信記事はありません。</div>
      <div class="message c-text-regular--m">
        記事を読んだりマークすると、おすすめ記事が配信されます。
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.related-news-preview {
  width: 280px;
  padding: 16px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  box-sizing: border-box;
  background: #ffffff;

  .title {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    .tooltip-icon {
      margin-left: 4px;
    }
  }

  .divider {
    height: 1px;
    background: $color-gray400;
    margin: 12px 0;
  }
  .related-news-page-link {
    display: flex;
    justify-content: center;
  }
  .empty-articles {
    .image-wrap {
      display: flex;
      justify-content: center;
      margin-bottom: 12px;
      .image {
        width: 100px;
        height: 100px;
      }
    }
  }
}
</style>
